/**
 * Dev-only replay switch. `?tourReset=1` on any workspace route puts the
 * record back to before the first run and the walkthrough, through the
 * onboarding API, as soon as the record has resolved: once per page load,
 * the param then stripped from the URL, the first run's latches cleared so
 * the question comes straight back when the override is on, and the shell
 * taken to Home. A button stays for the rest of the page load to reset
 * again.
 */

import { ROUTES } from "@/constants/routes";
import type { UserOnboarding } from "@cohesive/api-client";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resetFirstRunLatches } from "../first-run/decide";
import { useOnboardingRecord, useOnboardingStatus } from "./status";

export function resetPatch(record: UserOnboarding | undefined): UserOnboarding {
  const clear = (map: Record<string, string> | undefined) =>
    Object.fromEntries(Object.keys(map ?? {}).map((key) => [key, ""]));
  return {
    global: "",
    first_run: null,
    steps: clear(record?.steps),
    refs: clear(record?.refs),
  };
}

const PARAM = "tourReset";
const requested = () =>
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get(PARAM) === "1";
// Read as the app boots: a route redirect (`/canvas` → `/`) drops the query
// before the shell has mounted, so the switch cannot be read at render time.
let asked = requested();
// Once per page load; the button carries any further reset.
let applied = false;
export function resetTourResetForTest() {
  applied = false;
  asked = requested();
}

type State = "idle" | "busy" | "done" | "failed";

export function TourReset() {
  const status = useOnboardingStatus();
  const { record, stage } = useOnboardingRecord();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [armed, setArmed] = useState(() => applied || asked);
  const [state, setState] = useState<State>("idle");

  const reset = async () => {
    setState("busy");
    const ok = await status.patch(resetPatch(record));
    resetFirstRunLatches();
    setState(ok ? "done" : "failed");
    if (pathname !== ROUTES.HOME) navigate(ROUTES.HOME);
  };

  // The record has to be in hand: the patch blanks the steps and refs it holds.
  const ready = stage !== "unresolved";
  useEffect(() => {
    if (applied || !ready || !asked) return;
    applied = true;
    setArmed(true);
    const url = new URL(window.location.href);
    if (url.searchParams.has(PARAM)) {
      url.searchParams.delete(PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + url.search + url.hash,
      );
    }
    void reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  if (!import.meta.env.DEV || !armed) return null;

  return (
    <button
      type="button"
      onClick={() => void reset()}
      disabled={state === "busy" || !record}
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        // above the first run's layer, so a reset mid-primer is one click
        zIndex: 1001,
        padding: "8px 14px",
        borderRadius: 8,
        border: "1px solid rgba(0, 0, 0, 0.15)",
        background: "#fff",
        color: "#111",
        font: "500 13px ui-sans-serif, system-ui, sans-serif",
        cursor: "pointer",
      }}
    >
      {state === "busy"
        ? "Resetting…"
        : state === "done"
          ? "Walkthrough reset · Restart again"
          : state === "failed"
            ? "Reset failed · Try again"
            : "Restart walkthrough"}
    </button>
  );
}
